/**
 * 经脉面板
 */

import type { PlayerState } from '../engine/Player';
import { MERIDIANS, getTotalOpened, TOTAL_POINTS, type Meridian } from '../data/meridians';
import { getPlayerMeridians, calcPointCost, calcPointExpCost, calcPointSuccessRate } from '../engine/MeridianSystem';
import { findRealmLevel } from '../data/realms';

export type MeridianAction = 'open' | 'close';
export interface MeridianActionPayload { action: MeridianAction; meridianId?: string; }

export class MeridianPanel {
  private overlay: HTMLElement | null = null;
  private onAction: ((payload: MeridianActionPayload) => void) | null = null;

  setOnAction(cb: (p: MeridianActionPayload) => void): void { this.onAction = cb; }

  open(player: PlayerState): void {
    if (this.overlay) this.overlay.remove();
    const meridians = getPlayerMeridians(player);
    const opened = getTotalOpened(meridians);

    // 任督二脉判定
    const renOk = (meridians['ren'] ?? 0) >= 9;
    const duOk = (meridians['du'] ?? 0) >= 9;
    const xiaoZhouTian = renOk && duOk;
    const daZhouTian = xiaoZhouTian && (meridians['chong'] ?? 0) >= 7 && (meridians['dai'] ?? 0) >= 7;

    let cycleHtml = '';
    if (daZhouTian) cycleHtml = '<div style="color:var(--text-gold);text-align:center;margin-bottom:8px;">🌟 大周天 — 全属性+25% 修炼+20%</div>';
    else if (xiaoZhouTian) cycleHtml = '<div style="color:var(--text-gold);text-align:center;margin-bottom:8px;">⭐ 小周天 — 全属性+10%</div>';

    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';

    const list = MERIDIANS.map((m) => {
      const current = meridians[m.id] ?? 0;
      const total = m.points.length;
      const mastered = current >= total;
      const dots = Array.from({ length: total }, (_, i) =>
        i < current ? '●' : '○'
      ).join('');

      // 下一个穴位信息
      let nextInfo = '';
      if (!mastered) {
        const nextIdx = current;
        const point = m.points[nextIdx];
        const cost = calcPointCost(player, m.id, nextIdx);
        const expCost = calcPointExpCost(player, m.id, nextIdx);
        const rate = calcPointSuccessRate(player, m.id, nextIdx, false);
        const hasPill = player.inventory.some((i) => i.id === 'meridian_pill');
        const pillRate = hasPill ? calcPointSuccessRate(player, m.id, nextIdx, true) : rate;
        const bonusStr = getPointBonusStr(point);
        nextInfo = `
          <div style="font-size:0.75em;color:var(--text-secondary);margin-top:4px;">
            下一穴：${point.name} ${bonusStr}<br>
            灵石💰${cost} | 经验📖${expCost}% | 成功率${(rate*100).toFixed(0)}%
            ${hasPill ? `(用通脉丹→${(pillRate*100).toFixed(0)}%)` : ''}
          </div>
          <button class="btn btn-small btn-primary meridian-open-btn" data-id="${m.id}" style="margin-top:4px;">🔓 冲穴</button>
        `;
      }

      return `
        <div class="recipe-card" style="border-color:${mastered ? m.color : 'var(--border-color)'};${mastered ? `background:${m.color}10;` : ''}">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <span>${m.icon} <b>${m.name}</b></span>
            <span style="font-size:0.8em;color:${mastered ? 'var(--success)' : 'var(--text-secondary)'};">${current}/${total} ${mastered ? '✅贯通' : ''}</span>
          </div>
          <div style="font-size:0.8em;color:var(--text-secondary);">${dots}</div>
          <div style="font-size:0.7em;color:var(--text-secondary);">${m.desc.slice(0, 30)}...</div>
          ${nextInfo}
        </div>
      `;
    }).join('');

    this.overlay.innerHTML = `
      <div class="modal" style="max-width:520px;max-height:85vh;overflow-y:auto;">
        <h2>🔮 经脉 <span style="font-size:0.7em;color:var(--text-secondary);">${opened}/${TOTAL_POINTS}穴</span></h2>
        ${cycleHtml}
        <div style="display:flex;flex-direction:column;gap:6px;">${list}</div>
        <button class="btn btn-small" id="meridian-close" style="margin-top:12px;">关闭</button>
      </div>
    `;

    document.body.appendChild(this.overlay);

    this.overlay.querySelectorAll('.meridian-open-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset.id!;
        if (this.onAction) this.onAction({ action: 'open', meridianId: id });
      });
    });
    document.getElementById('meridian-close')?.addEventListener('click', () => { this.close(); if (this.onAction) this.onAction({ action: 'close' }); });
    this.overlay.addEventListener('click', (e) => { if (e.target === this.overlay) { this.close(); if (this.onAction) this.onAction({ action: 'close' }); } });
  }

  close(): void { if (this.overlay) { this.overlay.remove(); this.overlay = null; } }
}

function getPointBonusStr(point: any): string {
  const parts: string[] = [];
  if (point.hpBonus) parts.push(`HP+${point.hpBonus}`);
  if (point.mpBonus) parts.push(`MP+${point.mpBonus}`);
  if (point.attackBonus) parts.push(`攻+${point.attackBonus}`);
  if (point.defenseBonus) parts.push(`防+${point.defenseBonus}`);
  return parts.length > 0 ? `(${parts.join(' ')})` : '';
}
